import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { getIdsFromPath } from '@/utils';

import { useCurrentOrganisationStore } from '../store';

import useCurrentOrganization from './useCurrentOrganization';
import { QueryKeys } from './useHistory';

const getObservingQueryKeys = () => [
  QueryKeys.OBSERVING_PARTNER_QUERY_KEY,
  QueryKeys.OBSERVING_DISTRIBUTOR_QUERY_KEY,
  QueryKeys.OBSERVING_EMPLOYER_QUERY_KEY,
  QueryKeys.OBSERVING_SUBSIDIARY_QUERY_KEY,
];

interface Params {
  search: string;
  observingIds: (string | null)[];
  ownOrganizationPath?: string;
}

export const getValidObservingSearch = ({ search, observingIds, ownOrganizationPath }: Params) => {
  const { partnerId, distributorId, employerId, subsidiaryId } = getIdsFromPath(ownOrganizationPath);
  const ownIds = [partnerId, distributorId, employerId, subsidiaryId];

  const ids = observingIds.map((id, index) => id || ownIds[index]);

  const lastIndex = ids.findIndex((item, index) => !!item && index < ids.length - 1 && !ids[index + 1]);
  const isBroken = ids.some((item, index) => !item && index < ids.length - 1 && !!ids[index + 1]);

  if (!isBroken) {
    return { search, isChanged: false, resetFromIndex: ids.length };
  }

  const resetFromIndex = lastIndex + 1;
  const query = new URLSearchParams(search);
  getObservingQueryKeys()
    .slice(resetFromIndex)
    .forEach((key) => query.delete(key));

  return { search: query.toString(), isChanged: true, resetFromIndex };
};

const useCurrentOrganizationPathChecker = () => {
  const history = useHistory();
  const { search } = useLocation();
  const {
    observingPartnerId,
    observingDistributorId,
    observingEmployerId,
    observingSubsidiaryId,
    handleSetObservingPartnerId,
    handleSetObservingDistributorId,
    handleSetObservingEmployerId,
    handleSetObservingSubsidiaryId,
  } = useCurrentOrganisationStore();
  const { observingDefault } = useCurrentOrganization();

  const validSearch = useMemo(
    () =>
      getValidObservingSearch({
        search,
        observingIds: [observingPartnerId, observingDistributorId, observingEmployerId, observingSubsidiaryId],
        ownOrganizationPath: observingDefault.path,
      }),
    [
      search,
      observingPartnerId,
      observingDistributorId,
      observingEmployerId,
      observingSubsidiaryId,
      observingDefault.path,
    ]
  );

  useEffect(() => {
    if (validSearch.isChanged) {
      history.replace({ search: validSearch.search });
      [
        handleSetObservingPartnerId,
        handleSetObservingDistributorId,
        handleSetObservingEmployerId,
        handleSetObservingSubsidiaryId,
      ]
        .slice(validSearch.resetFromIndex)
        .forEach((setObservingId) => setObservingId(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validSearch]);
};

export default useCurrentOrganizationPathChecker;
